export const RAW_RESOURCES = ['food', 'wood', 'iron', 'bauxite', 'oil', 'silicon', 'uranium'] as const;
export const PROCESSED_RESOURCES = [
  'steel',
  'aluminum',
  'fuel',
  'semiconductors',
  'enrichedUranium',
] as const;
export const RESOURCES = [...RAW_RESOURCES, ...PROCESSED_RESOURCES] as const;

export type RawResourceId = (typeof RAW_RESOURCES)[number];
export type ProcessedResourceId = (typeof PROCESSED_RESOURCES)[number];
export type ResourceId = (typeof RESOURCES)[number];

/** Quantidades de recursos (matéria-prima e insumos). Chaves ausentes valem 0. */
export type ResourceBag = Partial<Record<ResourceId, number>>;

/** Custo: recursos (retirados do estoque do território) + dinheiro (do Tesouro). */
export interface Cost {
  resources: ResourceBag;
  money: number;
}

export const RESOURCE_ABBR: Record<ResourceId, string> = {
  food: 'ALI',
  wood: 'MAD',
  iron: 'FER',
  bauxite: 'BAU',
  oil: 'PET',
  silicon: 'SIL',
  uranium: 'URA',
  steel: 'AÇO',
  aluminum: 'ALU',
  fuel: 'CMB',
  semiconductors: 'SEM',
  enrichedUranium: 'UEN',
};

export const RESOURCE_NAMES: Record<ResourceId, string> = {
  food: 'Alimento',
  wood: 'Madeira',
  iron: 'Minério de ferro',
  bauxite: 'Bauxita',
  oil: 'Petróleo',
  silicon: 'Silício',
  uranium: 'Urânio',
  steel: 'Aço',
  aluminum: 'Alumínio',
  fuel: 'Combustível',
  semiconductors: 'Semicondutores',
  enrichedUranium: 'Urânio enriquecido',
};

/** Preço de referência ($) usado no mercado internacional, PIB e poder militar. */
export const REFERENCE_PRICES: Record<ResourceId, number> = {
  food: 1,
  wood: 1,
  iron: 1,
  bauxite: 1.5,
  oil: 2,
  silicon: 2,
  uranium: 10,
  steel: 3,
  fuel: 3,
  aluminum: 4,
  semiconductors: 8,
  enrichedUranium: 60,
};

const ABBR_TO_ID: Record<string, ResourceId> = Object.fromEntries(
  Object.entries(RESOURCE_ABBR).map(([id, abbr]) => [abbr, id as ResourceId]),
);

/** Separa "10 ALI" (custo) ou "ALI 10" (produção) em [quantidade, sigla]. */
function splitToken(part: string): [string | undefined, string | undefined] {
  const [a, b] = part.trim().split(/\s+/);
  return a !== undefined && /^[\d.,]+$/.test(a) ? [a, b] : [b, a];
}

/** Converte a notação do GDD ("10 ALI · 5 $" ou "PET 30 · MAD 15") em ResourceBag. */
export function parseBag(text: string): ResourceBag {
  const bag: ResourceBag = {};
  for (const part of text.split('·')) {
    if (!part.trim()) continue;
    const [qty, abbr] = splitToken(part);
    if (abbr === '$') continue;
    const id = abbr ? ABBR_TO_ID[abbr] : undefined;
    if (!id || qty === undefined) throw new Error(`Recurso inválido em "${text}"`);
    bag[id] = (bag[id] ?? 0) + Number(qty.replace('.', ''));
  }
  return bag;
}

/** Converte "25 AÇO · 40 $" em Cost. */
export function parseCost(text: string): Cost {
  let money = 0;
  for (const part of text.split('·')) {
    const [qty, abbr] = splitToken(part);
    if (abbr === '$' && qty) money += Number(qty.replace('.', ''));
  }
  return { resources: parseBag(text), money };
}

export function bagValue(bag: ResourceBag): number {
  let total = 0;
  for (const [id, qty] of Object.entries(bag)) total += (qty ?? 0) * REFERENCE_PRICES[id as ResourceId];
  return total;
}

export function costValue(cost: Cost): number {
  return cost.money + bagValue(cost.resources);
}
